import { useState, useRef, useEffect, useCallback } from 'react'

const PORTAL_ID = '21994206'
const FORM_GUID = '3f3ccf10-b0cc-4947-b66d-1748f7aef63d'
const HS_ENDPOINT = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_GUID}`

const AGENT_SCHEDULER = 'https://meetings.hubspot.com/kaylabragg/home-services'
const SCHEDULERS = {
    '$1-$500': AGENT_SCHEDULER,
    '$500-$3,000': AGENT_SCHEDULER,
    '$5,000+': AGENT_SCHEDULER,
}
const DEFAULT_SCHEDULER = AGENT_SCHEDULER

const BUDGET_OPTIONS = [
    { value: '$1-$500', display: 'Less than $1,000' },
    { value: '$500-$3,000', display: '$1,000 - $5,000' },
    { value: '$5,000+', display: '$5,000+' },
]

function formatPhone(val) {
    const d = (val || '').replace(/\D/g, '').slice(0, 10)
    if (d.length < 4) return d
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

export default function BookingForm({
    variant = 'dark',
    containerId,
    onSubmitSuccess,
}) {
    const [fullName, setFullName] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [budget, setBudget] = useState('')
    const [budgetDisplay, setBudgetDisplay] = useState('')
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [errors, setErrors] = useState({})
    const [submitted, setSubmitted] = useState(false)

    const dropdownRef = useRef(null)
    const wrapRef = useRef(null)

    useEffect(() => {
        function handleClickOutside(e) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setDropdownOpen(false)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    const clearError = useCallback((field) => {
        setErrors((prev) => ({ ...prev, [field]: '' }))
    }, [])

    function handleSubmit() {
        const nameParts = fullName.trim().replace(/\s+/g, ' ').split(' ')
        const firstName = nameParts.shift() || ''
        const lastName = nameParts.join(' ')
        const emailVal = email.trim()
        const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(emailVal)
        const phoneVal = phone.trim()
        const phoneDigits = phoneVal.replace(/\D/g, '')
        const phoneOk = !phoneVal || phoneDigits.length === 10

        const newErrors = {}
        if (!firstName || !lastName)
            newErrors.fullName = 'Please enter your first and last name.'
        if (!emailOk) newErrors.email = 'Please enter a complete email address.'
        if (!phoneOk) newErrors.phone = 'Please enter a 10-digit phone number.'
        if (!budget)
            newErrors.budget = 'Please select your monthly marketing budget.'

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        const payload = {
            fields: [
                { name: 'firstname', value: firstName },
                { name: 'lastname', value: lastName },
                { name: 'email', value: emailVal },
                { name: 'phone', value: phoneVal },
                {
                    name: 'what_is_your_monthly_marketing_budget_',
                    value: budget,
                },
            ],
            context: {
                pageUri: window.location.href,
                pageName: document.title,
            },
        }

        fetch(HS_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {})

        const params = new URLSearchParams()
        params.set('embed', 'true')
        if (firstName) {
            params.set('firstname', firstName)
            params.set('firstName', firstName)
        }
        if (lastName) {
            params.set('lastname', lastName)
            params.set('lastName', lastName)
        }
        params.set('email', emailVal)
        if (phoneVal) params.set('phone', phoneVal)
        params.set('what_is_your_monthly_marketing_budget_', budget)

        const base = SCHEDULERS[budget] || DEFAULT_SCHEDULER
        const url = `${base}?${params.toString()}`

        setSubmitted(true)

        setTimeout(() => {
            const wrap = wrapRef.current
            if (wrap) {
                wrap.innerHTML = `<div class="meetings-iframe-container" data-src="${url}" style="min-height:780px;"></div>`
                const s = document.createElement('script')
                s.src =
                    'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
                document.body.appendChild(s)
            }
            if (onSubmitSuccess) onSubmitSuccess()
        }, 400)
    }

    if (submitted) {
        return (
            <div
                ref={wrapRef}
                id={containerId}
                style={{ minHeight: '780px' }}
            />
        )
    }

    const isDark = variant === 'dark'
    const labelColor = isDark ? '#9ca3af' : '#1a1d23'
    const inputBorder = isDark ? 'transparent' : '#f0ddc9'
    const errorColor = isDark ? '#fab331' : '#d14a1f'
    const placeholderDefault = isDark ? '' : 'Select a range'

    const inputStyle = {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: isDark ? '14px 16px' : '14px 16px 14px 42px',
        borderRadius: isDark ? '10px' : '12px',
        border: `1px solid ${inputBorder}`,
        background: '#ffffff',
        color: '#1a1d23',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        fontFamily: 'inherit',
    }

    const darkInputStyle = {
        ...inputStyle,
        padding: '14px 16px',
    }

    const labelStyle = {
        color: labelColor,
        fontSize: '0.72rem',
        fontWeight: 800,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    }

    function handleFocus(e) {
        e.target.style.borderColor = '#ED5C2F'
        e.target.style.boxShadow = `0 0 0 3px rgba(237,92,47,${
            isDark ? '0.22' : '0.18'
        })`
    }

    function handleBlur(e) {
        e.target.style.borderColor = inputBorder
        e.target.style.boxShadow = 'none'
    }

    return (
        <div ref={wrapRef} id={containerId}>
            <div
                style={{
                    background: isDark ? '#363b45' : 'transparent',
                    padding: isDark ? '32px' : '0',
                    borderRadius: isDark ? '18px' : '0',
                    textAlign: 'left',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: isDark ? '22px 20px' : '18px 16px',
                    boxSizing: 'border-box',
                    width: '100%',
                }}
                className="agents-form-grid">
                <label
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: 0,
                    }}>
                    <span style={labelStyle}>Full Name</span>
                    {!isDark ? (
                        <span
                            style={{ position: 'relative', display: 'block' }}>
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ED5C2F"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    position: 'absolute',
                                    left: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                }}>
                                <circle cx="12" cy="8" r="4" />
                                <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                            </svg>
                            <input
                                placeholder="First and last name"
                                value={fullName}
                                onChange={(e) => {
                                    setFullName(e.target.value)
                                    clearError('fullName')
                                }}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{
                                    ...inputStyle,
                                    borderColor: errors.fullName
                                        ? errorColor
                                        : inputBorder,
                                }}
                            />
                        </span>
                    ) : (
                        <input
                            placeholder="First and last name"
                            value={fullName}
                            onChange={(e) => {
                                setFullName(e.target.value)
                                clearError('fullName')
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            style={{
                                ...darkInputStyle,
                                borderColor: errors.fullName
                                    ? errorColor
                                    : inputBorder,
                            }}
                        />
                    )}
                    {errors.fullName && (
                        <span
                            style={{
                                color: errorColor,
                                fontSize: '0.78rem',
                                fontWeight: 600,
                            }}>
                            {errors.fullName}
                        </span>
                    )}
                </label>

                <label
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: 0,
                    }}>
                    <span style={labelStyle}>Phone Number</span>
                    {!isDark ? (
                        <span
                            style={{ position: 'relative', display: 'block' }}>
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ED5C2F"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    position: 'absolute',
                                    left: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                }}>
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
                            </svg>
                            <input
                                type="tel"
                                inputMode="tel"
                                maxLength={14}
                                placeholder="(555) 123-4567"
                                value={phone}
                                onChange={(e) => {
                                    setPhone(formatPhone(e.target.value))
                                    clearError('phone')
                                }}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{
                                    ...inputStyle,
                                    borderColor: errors.phone
                                        ? errorColor
                                        : inputBorder,
                                }}
                            />
                        </span>
                    ) : (
                        <input
                            type="tel"
                            inputMode="tel"
                            maxLength={14}
                            placeholder="(555) 123-4567"
                            value={phone}
                            onChange={(e) => {
                                setPhone(formatPhone(e.target.value))
                                clearError('phone')
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            style={{
                                ...darkInputStyle,
                                borderColor: errors.phone
                                    ? errorColor
                                    : inputBorder,
                            }}
                        />
                    )}
                    {errors.phone && (
                        <span
                            style={{
                                color: errorColor,
                                fontSize: '0.78rem',
                                fontWeight: 600,
                            }}>
                            {errors.phone}
                        </span>
                    )}
                </label>

                <label
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: 0,
                    }}>
                    <span style={labelStyle}>Email</span>
                    {!isDark ? (
                        <span
                            style={{ position: 'relative', display: 'block' }}>
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ED5C2F"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    position: 'absolute',
                                    left: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                }}>
                                <rect
                                    x="3"
                                    y="5"
                                    width="18"
                                    height="14"
                                    rx="2"
                                />
                                <path d="M3 7l9 6 9-6" />
                            </svg>
                            <input
                                type="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value)
                                    clearError('email')
                                }}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{
                                    ...inputStyle,
                                    borderColor: errors.email
                                        ? errorColor
                                        : inputBorder,
                                }}
                            />
                        </span>
                    ) : (
                        <input
                            type="email"
                            placeholder="you@company.com"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value)
                                clearError('email')
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            style={{
                                ...darkInputStyle,
                                borderColor: errors.email
                                    ? errorColor
                                    : inputBorder,
                            }}
                        />
                    )}
                    {errors.email && (
                        <span
                            style={{
                                color: errorColor,
                                fontSize: '0.78rem',
                                fontWeight: 600,
                            }}>
                            {errors.email}
                        </span>
                    )}
                </label>

                <label
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: 0,
                    }}>
                    <span style={labelStyle}>Monthly Marketing Budget</span>
                    <div
                        ref={dropdownRef}
                        style={{ position: 'relative', minWidth: 0 }}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                setDropdownOpen(!dropdownOpen)
                            }}
                            style={{
                                width: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                padding: '14px 16px',
                                borderRadius: isDark ? '10px' : '12px',
                                border: `1px solid ${
                                    errors.budget
                                        ? errorColor
                                        : dropdownOpen
                                        ? '#ED5C2F'
                                        : inputBorder
                                }`,
                                background: '#ffffff',
                                color: budgetDisplay ? '#1a1d23' : '#9ca3af',
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition:
                                    'border-color 0.15s, box-shadow 0.15s',
                                boxShadow: dropdownOpen
                                    ? '0 0 0 3px rgba(237,92,47,0.22)'
                                    : 'none',
                            }}>
                            <span
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                {budgetDisplay ||
                                    (isDark
                                        ? 'What is your monthly marketing budget?'
                                        : placeholderDefault)}
                            </span>
                            <svg
                                width="14"
                                height="9"
                                viewBox="0 0 14 9"
                                fill="none"
                                style={{
                                    flexShrink: 0,
                                    transition: 'transform 0.18s ease',
                                    transform: dropdownOpen
                                        ? 'rotate(180deg)'
                                        : 'none',
                                }}>
                                <path
                                    d="M1 1l6 6 6-6"
                                    stroke="#ED5C2F"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {dropdownOpen && (
                            <ul
                                role="listbox"
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 6px)',
                                    left: 0,
                                    right: 0,
                                    zIndex: 30,
                                    margin: 0,
                                    padding: '6px',
                                    listStyle: 'none',
                                    background: '#ffffff',
                                    border: isDark
                                        ? 'none'
                                        : '1px solid #f0ddc9',
                                    borderRadius: '12px',
                                    boxShadow:
                                        '0 18px 40px rgba(0,0,0,0.32), 0 2px 6px rgba(0,0,0,0.18)',
                                    overflow: 'hidden',
                                }}>
                                {BUDGET_OPTIONS.map((opt) => (
                                    <li
                                        key={opt.value}
                                        role="option"
                                        tabIndex={0}
                                        onClick={() => {
                                            setBudget(opt.value)
                                            setBudgetDisplay(opt.display)
                                            setDropdownOpen(false)
                                            clearError('budget')
                                        }}
                                        onMouseOver={(e) =>
                                            (e.currentTarget.style.background =
                                                '#fff1e8')
                                        }
                                        onMouseOut={(e) =>
                                            (e.currentTarget.style.background =
                                                'transparent')
                                        }
                                        style={{
                                            padding: '11px 14px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            color: '#1a1d23',
                                            fontSize: '0.95rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '10px',
                                            transition:
                                                'background-color 0.12s',
                                        }}>
                                        {opt.display}
                                        {budget === opt.value && (
                                            <span
                                                style={{
                                                    color: '#ed5c2f',
                                                    fontWeight: 800,
                                                }}>
                                                &#10003;
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {errors.budget && (
                        <span
                            style={{
                                color: errorColor,
                                fontSize: '0.78rem',
                                fontWeight: 600,
                            }}>
                            {errors.budget}
                        </span>
                    )}
                </label>

                {isDark ? (
                    <div
                        className="agents-form-footer"
                        style={{
                            gridColumn: '1 / -1',
                            minWidth: 0,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            marginTop: '6px',
                        }}>
                        <span
                            style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                            We&apos;ll use this to route your request and prep
                            for the call.
                        </span>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            style={{
                                padding: '14px 28px',
                                border: 'none',
                                borderRadius: '999px',
                                background:
                                    'linear-gradient(135deg, #ed5c2f, #f39e5b)',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                cursor: 'pointer',
                                boxShadow:
                                    '0 0 8px rgba(237,92,47,0.25), 0 1px 3px rgba(237,92,47,0.2)',
                                transition: 'box-shadow 0.15s ease',
                                fontFamily: 'inherit',
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.boxShadow =
                                    '0 0 12px rgba(237,92,47,0.4), 0 2px 6px rgba(237,92,47,0.3)')
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.boxShadow =
                                    '0 0 8px rgba(237,92,47,0.25), 0 1px 3px rgba(237,92,47,0.2)')
                            }>
                            Book a strategy call &rarr;
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        style={{
                            gridColumn: '1 / -1',
                            marginTop: '8px',
                            padding: '18px 28px',
                            border: 'none',
                            borderRadius: '999px',
                            background:
                                'linear-gradient(135deg, #ed5c2f, #f39e5b)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '1rem',
                            cursor: 'pointer',
                            boxShadow:
                                '0 0 8px rgba(237,92,47,0.25), 0 1px 3px rgba(237,92,47,0.2)',
                            transition: 'box-shadow 0.15s ease',
                            fontFamily: 'inherit',
                        }}
                        onMouseOver={(e) =>
                            (e.currentTarget.style.boxShadow =
                                '0 0 12px rgba(237,92,47,0.4), 0 2px 6px rgba(237,92,47,0.3)')
                        }
                        onMouseOut={(e) =>
                            (e.currentTarget.style.boxShadow =
                                '0 0 8px rgba(237,92,47,0.25), 0 1px 3px rgba(237,92,47,0.2)')
                        }>
                        Book my call &rarr;
                    </button>
                )}
            </div>
        </div>
    )
}
